import classNames from "classnames";
import { AppointmentPhase, Status } from "components";
import PublicationToWebIcon from "components/PublicationToWeb/PublicationToWebIcon";
import { AppointmentSidePanelPlanner } from "components/SidePanel/Appointment/AppointmentSidePanel";
import VisibleToExternalIcon from "components/VisibleToExternal/VisibleToExternalIcon";
import moment from "moment";
import { PlanningContext } from "PlanningContext/PlanningContext";
import React, { useState } from "react";
import ShowTime from "show/components/ShowTime";
import { DocumentIndications } from "show/list/tableLayout";

interface DayHeaderProps {
  day: Date;
  entryCount: number;
}

const DayHeader = (props: DayHeaderProps) => {
  const planningContext = React.useContext(PlanningContext);
  const { day, entryCount } = props;
  const date = moment.utc(day);
  // We need entryCount * 2 since every entry has a divider
  const count = entryCount === 0 ? 1 : entryCount * 2 - 1;

  return <div
    style={{ gridRow: `span ${count}` }}
    role="button"
    onClick={() => planningContext.open({ date })}
    className={classNames("quarterly-appointment-list-day-header", {
      today: date.isSame(moment().utc(), "day")
    })}
  >
    <span>{date.format("ddd D")}</span>
  </div>
};

interface QuarterAppointmentTimeProps {
  day: Date;
  dateTime: server.dto.AppointmentDateTime;
}

const QuarterAppointmentTime = (props: QuarterAppointmentTimeProps) => {
  const start = moment.utc(props.dateTime.startDisplay);
  const end = moment.utc(props.dateTime.endDisplay);

  return <>
    <ShowTime dateTime={props.dateTime} />
    {!start.isSame(props.day, "day") && <span>« </span>}
    {!end.isSame(props.day, "day") && <span> »</span>}
  </>
};

interface QuarterDayProps {
  day: server.dto.PlannerDay;
  hoverId: number | null;
  onHover: (id: number | null) => void;
}

// TODO: Add validated indicator (to month and week as well)
const QuarterDay = (props: QuarterDayProps) => {
  const { day, hoverId, onHover } = props;

  const onMouseOver = (appointment: server.dto.PlannerAppointment) => {
    onHover(getAppointmentId(appointment));
  };

  const onMouseOut = () => {
    onHover(null);
  };

  const isInternalUser = site.currentEnvironment.type === "internal";

  return <>
    <DayHeader day={day.date} entryCount={day.appointments.length} />
    {day.appointments.map((appointment, index) => <React.Fragment key={index}>
      <AppointmentSidePanelPlanner appointment={appointment}>
        <div
          onMouseOut={onMouseOut}
          onMouseOver={() => onMouseOver(appointment)}
          className={classNames("quarterly-appointment-list-day", {
            "quarterly-appointment-list-day-hover": hoverId === getAppointmentId(appointment)
          })}
        >
          <div className="quarterly-appointment-list-day-entry">
            <QuarterAppointmentTime day={day.date} dateTime={appointment.dateTime} />
          </div>
          <div className="quarterly-appointment-list-day-entry flex gap">
            <i className={appointment.type.icon} />
            <AppointmentPhase value={appointment.phase} displayLabel={false} />
          </div>
          <div className="quarterly-appointment-list-day-entry flex-column">
            <span>{appointment.summary}</span>
            {appointment.description && <span>{appointment.description}</span>}
            {appointment.status.title && <Status status={appointment.status} />}
          </div>
          <div className="quarterly-appointment-list-day-entry">
            {appointment.location}
          </div>
          <div className="quarterly-appointment-list-day-entry">
            {appointment.artists.length > 0 && <React.Fragment key={index}>
              <div>{appointment.artists[0].name}</div>
              <div className="s1_gray">{appointment.contacts.map(c => c.name.fullName).join(", ")}</div>
            </React.Fragment>}
          </div>
          {appointment.salesExcludingTax && <div className="quarterly-appointment-list-day-entry s1_cell_financialamount">
            {(appointment.type.value === "show" && appointment.salesExcludingTax.amount !== 0) && appointment.salesExcludingTax.formatted}
          </div>}
          <div className="quarterly-appointment-list-day-entry flex gap">
            {appointment.type.value === "show" && <>
              {isInternalUser && <VisibleToExternalIcon visibleToExternal={appointment.visibleToExternal} />}
              <PublicationToWebIcon publicationToWeb={appointment.publicationToWeb} />
            </>}
          </div>
        </div>
      </AppointmentSidePanelPlanner>
      <div>
        {appointment.type.value === "show" && <>
          <DocumentIndications
            documentIndications={appointment.documentIndications}
            showId={appointment.showId}
          />
        </>}
      </div>
      {index !== day.appointments.length - 1 && <div className="quarterly-appointment-list-entry-divider" />}
    </React.Fragment>)}
    <div className="quarterly-appointment-list-day-divider" />
  </>;
};

interface QuarterMonthProps {
  items: server.dto.PlannerDay[];
  hoverId: number | null;
  onHover: (id: number | null) => void;
}

const QuarterMonth = (props: QuarterMonthProps) => {
  const { items, hoverId, onHover } = props;
  const [collapsed, setCollapsed] = useState(false);

  return <>
    <div onClick={() => setCollapsed(!collapsed)} className="quarterly-appointment-list-month-header">
      <span className="info-text-medium">{moment.utc(items[0].date).format("MMMM YYYY")}</span>
    </div>
    {!collapsed && items.map((item, index) => <QuarterDay
      key={index}
      day={item}
      hoverId={hoverId}
      onHover={onHover}
    />)}
  </>
};

interface QuarterlyAppointmentListProps {
  items: server.dto.PlannerDay[];
}

export const QuarterlyAppointmentList = (props: QuarterlyAppointmentListProps) => {
  const { items } = props;
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  const onHover = (id: number | null) => {
    setHoveredId(id);
  }

  const byMonth = groupItemsByMonth(items);

  return <div className="quarterly-appointment-list-grid">
    {byMonth.map((month, index) => <QuarterMonth
      key={index}
      items={month}
      hoverId={hoveredId}
      onHover={onHover}
    />)}
  </div>
};

const groupItemsByMonth = (items: server.dto.PlannerDay[]): server.dto.PlannerDay[][] => {
  const byMonth: server.dto.PlannerDay[][] = [];

  let currentMonth = moment.utc(items[0].date).month();
  let days: server.dto.PlannerDay[] = [];

  for (const item of items) {
    const month = moment.utc(item.date).month();
    if (month !== currentMonth) {
      currentMonth = month;
      byMonth.push(days);
      days = [];
    }
    days.push(item);
  }

  if (days.length > 0) {
    byMonth.push(days);
  }

  return byMonth;
};

// TODO: This should be moved to a utility file
const getAppointmentId = (appointment: server.dto.PlannerAppointment) => {
  switch (appointment.type.value) {
    case "externalCalendar":
      return appointment.externalEventId;
    case "show":
      return appointment.showId;
    case "flight":
      return appointment.flightId;
    case "train":
      return appointment.trainSegmentId;
    case "groundTransport":
      return appointment.groundTransportId;
  }
};